<script setup lang="ts">
import MarkdownEditor from '@/sharedComponents/MarkdownEditor/MarkdownEditor.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import DividerLine from '@/uiKit/DividerLine.vue'
import HotKey from '@/uiKit/HotKey.vue'
import ListMenuContainer from '@/uiKit/ListMenuContainer.vue'
// eslint-disable-next-line no-restricted-imports
import PopupMenu from '@/uiKit/PopupMenu.vue'
import type { AutoPlacementOptions } from '@floating-ui/core'
import type { OffsetOptions } from '@floating-ui/vue'
import { computed, ref } from 'vue'
/**
 * Renders a popup menu that allows the user to bulk update fields
 * for a given list of entities
 */

defineProps<{
  /** Indicates where the popup can be placed relative to its trigger */
  placement?: AutoPlacementOptions
  /** Used to adjust the position of the popup relative to its trigger */
  offset?: OffsetOptions
  /** Optional CSS selector to teleport the popup to */
  teleportTo?: string
}>()

defineEmits<{ startCase: [string] }>()

const isOpen = ref(false)
const prompt = ref('')

const isDisabled = computed(() => !prompt.value || prompt.value.trim() === '')
</script>

<template>
  <PopupMenu
    :open="isOpen"
    :auto-placement="placement"
    :offset="offset"
    trigger-element="div"
    :teleport-to="teleportTo"
    @change:open="isOpen = $event"
  >
    <template #trigger>
      <slot
        name="trigger"
        :is-open="isOpen"
        :toggle-open="() => (isOpen = !isOpen)"
      />
    </template>
    <template #dropdown>
      <ListMenuContainer role="dialog">
        <div class="go-scrollbar flex h-32 max-h-32 min-w-48 flex-col overflow-y-auto p-1">
          <MarkdownEditor
            value=""
            autofocus
            class="h-max grow resize-none p-1"
            placeholder="What do you want to do with your selection?"
            aria-label="Initial question"
            @submit="$emit('startCase', prompt)"
            @input="prompt = $event"
          />
        </div>
        <DividerLine color="subtle" />
        <DarwinButton
          size="sm"
          variant="neutral"
          class="m-1"
          :disabled="isDisabled"
          aria-label="Start a case"
          @click="$emit('startCase', prompt)"
        >
          <div class="flex items-center gap-1.5">
            <div>Start a case</div>
            <HotKey
              hotkey="Enter"
              size="xs"
              :disabled="isDisabled"
            />
          </div>
        </DarwinButton>
      </ListMenuContainer>
    </template>
  </PopupMenu>
</template>
